import * as React from 'react';
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/Tooltip';
import { cn } from '@/lib/cn';
import { runeLength } from '@/lib/copyValidation';
import type { CopyLocale } from '@/schemas/admin/copy';
import { LOCALE_DIR, preferMultiline } from './StringEditor';

interface StringListEditorProps {
  id: string;
  locale: CopyLocale;
  value: readonly string[];
  onChange: (next: string[]) => void;
  /** The shipped list for this locale — also fixes the item count. */
  baseline: readonly string[];
  maxLen: number | null;
  /** Keyed by leaf path (`[0]`), matching `validateCopyValue` issue fields. */
  fieldErrors?: Record<string, string>;
  label: React.ReactNode;
}

/**
 * The `string_list` shape — 6 keys, e.g. `safety.anti` (6 rules).
 *
 * The item count is part of the key's contract with the frontend, so add and
 * remove are rendered but disabled: an editor must be able to see that the
 * list is fixed, not merely discover it when the server rejects the save.
 */
export function StringListEditor({
  id,
  locale,
  value,
  onChange,
  baseline,
  maxLen,
  fieldErrors,
  label,
}: StringListEditorProps) {
  const { t } = useTranslation('copy');
  const dir = LOCALE_DIR[locale];
  const limit = maxLen && maxLen > 0 ? maxLen : null;
  const count = Math.max(value.length, baseline.length);

  function updateAt(index: number, next: string) {
    const copy = [...value];
    while (copy.length < count) copy.push('');
    copy[index] = next;
    onChange(copy);
  }

  return (
    <div className="mb-4" role="group" aria-labelledby={`${id}-label`}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p id={`${id}-label`} className="text-sm font-semibold text-[color:var(--color-fg)]">
          {label}
        </p>
        <FixedCountNotice count={count} />
      </div>

      <ol className="space-y-3">
        {Array.from({ length: count }, (_, i) => {
          const item = value[i] ?? '';
          const base = baseline[i] ?? '';
          const error = fieldErrors?.[`[${i}]`];
          const used = runeLength(item);
          const over = limit != null && used > limit;
          const inputId = `${id}-${i}`;
          const errorId = `${inputId}-error`;
          const multiline = preferMultiline(base) || preferMultiline(item);
          return (
            <li
              key={i}
              className="rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-3"
            >
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <label
                  htmlFor={inputId}
                  className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-fg-muted)]"
                >
                  {t('editor.itemN', { n: i + 1 })}
                </label>
                <span
                  className={cn(
                    'text-[11px] text-[color:var(--color-fg-muted)]',
                    over && 'font-semibold text-[color:var(--color-danger)]'
                  )}
                >
                  {limit != null
                    ? t('editor.charCount', { used, limit })
                    : t('editor.charCountNoLimit', { used })}
                </span>
              </div>
              {multiline ? (
                <textarea
                  id={inputId}
                  dir={dir}
                  rows={3}
                  value={item}
                  onChange={(e) => updateAt(i, e.target.value)}
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? errorId : undefined}
                  className={FIELD_CLASS}
                />
              ) : (
                <input
                  id={inputId}
                  dir={dir}
                  value={item}
                  onChange={(e) => updateAt(i, e.target.value)}
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? errorId : undefined}
                  className={cn(FIELD_CLASS, 'h-11')}
                />
              )}
              {error ? (
                <p
                  id={errorId}
                  role="alert"
                  className="mt-1 text-xs font-medium text-[color:var(--color-danger)]"
                >
                  {error}
                </p>
              ) : null}
              {base ? (
                <p
                  dir={dir}
                  className="mt-1.5 whitespace-pre-wrap text-xs text-[color:var(--color-fg-muted)]"
                >
                  <span className="me-1 font-semibold uppercase tracking-wide">
                    {t('editor.baselineLabel')}
                  </span>
                  {base}
                </p>
              ) : null}
            </li>
          );
        })}
      </ol>

      <div className="mt-3 flex items-center gap-2">
        <LockedAction id={`${id}-add`} label={t('editor.addItem')} reason={t('editor.fixedCountReason')} />
        <LockedAction
          id={`${id}-remove`}
          label={t('editor.removeItem')}
          reason={t('editor.fixedCountReason')}
        />
      </div>
    </div>
  );
}

const FIELD_CLASS =
  'block w-full rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-3 text-sm text-[color:var(--color-fg)] shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)] focus-visible:ring-offset-2 aria-[invalid=true]:border-[color:var(--color-danger)]';

function FixedCountNotice({ count }: { count: number }) {
  const { t } = useTranslation('copy');
  return (
    <span className="inline-flex items-center gap-1 text-xs text-[color:var(--color-fg-muted)]">
      <Lock className="size-3.5" aria-hidden />
      {t('editor.fixedCount', { count })}
    </span>
  );
}

/**
 * A disabled control still has to explain itself. `aria-disabled` rather than
 * `disabled` keeps it focusable, so the tooltip is reachable by keyboard.
 */
export function LockedAction({
  id,
  label,
  reason,
}: {
  id: string;
  label: string;
  reason: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          size="sm"
          aria-disabled
          aria-label={`${label} — ${reason}`}
          onClick={(e) => e.preventDefault()}
          className="cursor-not-allowed opacity-60"
        >
          <Lock className="size-4" aria-hidden />
          {label}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{reason}</TooltipContent>
    </Tooltip>
  );
}
